import { Temporal } from 'temporal-polyfill';

export function getToday(): Temporal.PlainDate {
	return Temporal.Now.plainDate(Temporal.Calendar.from('iso8601'));
}

export function getNow(): Temporal.PlainDateTime {
	return Temporal.Now.plainDateTimeISO();
}

export function getCurrentTime(): Temporal.PlainTime {
	return Temporal.Now.plainTimeISO();
}

export function getStartOfTheWeek(date: Temporal.PlainDate, weekStartsOn = 1): Temporal.PlainDate {
	// Temporal's dayOfWeek uses 1 for Monday and 7 for Sunday
	const dayOfWeek = date.dayOfWeek;
	// Calculate how many days to subtract to get to the start of the week
	const daysToSubtract = (dayOfWeek - weekStartsOn + 7) % 7;
	return date.subtract({ days: daysToSubtract });
}

export function getEndOfTheWeek(date: Temporal.PlainDate, weekStartsOn = 1): Temporal.PlainDate {
	// Get the start of the week for the given date
	const startOfWeek = getStartOfTheWeek(date, weekStartsOn);
	// Add 6 days to the start of the week to find the end of the week
	// Since a week is 7 days long, the end of the week is 6 days after the start
	return startOfWeek.add({ days: 6 });
}

export function getEndOfTheMonth(date: Temporal.PlainDate): Temporal.PlainDate {
	return new Temporal.PlainDate(date.year, date.month, date.daysInMonth);
}

export function getStartOfTheMonth(date: Temporal.PlainDate): Temporal.PlainDate {
	return new Temporal.PlainDate(date.year, date.month, 1);
}

export function getTimeAgo(date: Date): string {
	const now = Temporal.Now.instant();
	const past = Temporal.Instant.from(date.toISOString());

	const elapsedSeconds = now.since(past).total({ unit: 'seconds' });

	const days = Math.floor(elapsedSeconds / 86400);
	const hours = Math.floor((elapsedSeconds % 86400) / 3600);
	const minutes = Math.floor((elapsedSeconds % 3600) / 60);
	const seconds = Math.floor(elapsedSeconds % 60);

	if (days > 0) return `${days}d ago`;
	if (hours > 0) return `${hours}h ago`;
	if (minutes > 0) return `${minutes}m ago`;
	return `${seconds}s ago`;
}

export function areInstantsAreInTheSameDay(args: {
	instant1: Temporal.Instant;
	instant2: Temporal.Instant;
	timeZone?: string;
}) {
	const tz = args.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

	const date1PlainDate = args.instant1.toZonedDateTimeISO(Temporal.TimeZone.from(tz)).toPlainDate();
	const date2PlainDate = args.instant2.toZonedDateTimeISO(Temporal.TimeZone.from(tz)).toPlainDate();

	return date1PlainDate.equals(date2PlainDate);
}

export function isPlainDateTimeInThePresentOrPast(args: {
	date: Temporal.PlainDateTime;
	current?: Temporal.PlainDateTime;
	timeZone?: string;
}) {
	const tz = args.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
	return (
		Temporal.PlainDateTime.compare(args.date, args.current ? args.current : Temporal.Now.plainDateTimeISO(tz)) <= 0
	);
}

export function isStartOfTheWeek(day: Temporal.PlainDate, weekStartsOn: 1 | 7 = 1) {
	return weekStartsOn === 1 ? day.dayOfWeek === 1 : day.dayOfWeek === 7;
}

export function isEndOfTheWeek(day: Temporal.PlainDate, weekStartsOn: 1 | 7 = 1) {
	return weekStartsOn === 1 ? day.dayOfWeek === 7 : day.dayOfWeek === 6;
}

export interface IDay {
	plainDate: Temporal.PlainDate;
}

interface IGenerateMonthArgs {
	/**
	 * Year and month to view
	 */
	monthYear: Temporal.PlainYearMonth;
	/**
	 * 1 for Monday and 7 for Sunday
	 */
	weekStartsOn: 1 | 7;
}

/**
 * Returns 42 numbers with the given days corresponding to the month calendar for the given date.
 * @param date
 */
export function generateMonthDays(args: IGenerateMonthArgs): Array<IDay> {
	const result: Array<IDay> = [];
	const startDate = Temporal.PlainDate.from({ year: args.monthYear.year, month: args.monthYear.month, day: 1 });

	/**
	 *  Adjusting startOfCalendar to account for weeks starting on Monday or Sunday.
	 *  If dayOfWeek is 7 (Sunday), we want to start from this day, otherwise, calculate the offset to the previous Sunday
	 */
	let offset = 0;
	if (args.weekStartsOn === 1) {
		offset = startDate.dayOfWeek - 1;
	} else if (args.weekStartsOn === 7) {
		startDate.dayOfWeek === 7 ? 0 : startDate.dayOfWeek;
	}
	const startOfCalendar = startDate.subtract({ days: offset });

	for (let i = 0; i < 42; i++) {
		const plainDate = startOfCalendar.add({ days: i });
		result.push({ plainDate });
	}

	return result;
}

export function isSameWeek(date1: Temporal.PlainDate, date2: Temporal.PlainDate, weekStartsOn: number = 1): boolean {
	// Calculate the start of the week for both dates
	const startOfWeek1 = getStartOfTheWeek(date1, weekStartsOn);
	const startOfWeek2 = getStartOfTheWeek(date2, weekStartsOn);

	// Check if the start of the week is the same for both dates
	return startOfWeek1.equals(startOfWeek2);
}
